import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DisplayAPIsException from "../../../../BasicComponents/DisplayAPIsExceptionAlert";
import { LineChartComponent } from "../../../../ChartComponents/LineChartFluent";
import { logEvent } from "../../../../helper/AppInsightsHelper";
import { CustomShimmer } from "../../../../helper/CustomShimmer";
import { ProductViewFiltersContext } from "../../../../pages/ProductView";
import CustomCard from "../../../../SharedComponents/CustomCard";
import DisplayEmptyDataMessage from "../../../../SharedComponents/DisplayEmptyDataMessage";
import { getFeatureDataSelector } from "../../../../slices/ExploreProducts.slice";
import { selectViewport } from "../../../../slices/UI.slice";
import { useAppSelector } from "../../../../store/hooks";
import { getLineChartData, groupBy } from "../../../../Utils/js-common";
import { PRODUCT_VIEW_DATA } from "../../Common/ProductView.constants";
import { FeaturesByNameProps, ILineChartData } from "../../Common/ProductView.interfaces";
import { PRODUCT_VIEW_STYLES } from "../../Common/ProductView.styles";
import GranularSummaryChart from "./GranularSummaryChart";

export const SummaryTab = () => {
  const [width, setWidth] = useState<number | undefined>(undefined);
  const lineMetricsContainerRef = useRef<HTMLDivElement>(null);
  const resizedWidth = useAppSelector((state) => selectViewport(state).offsetWidth);
  const { data, isLoading, isSuccess, isError, error } = useAppSelector(getFeatureDataSelector);
  const filters = useContext(ProductViewFiltersContext);
  const displayAPIsException = isError && <DisplayAPIsException error={error} height={345} />;
  const [searchParams] = useSearchParams();
  const productName = searchParams.get("productname");
  const isPurviewProduct = productName?.toLowerCase().includes("purview");
  const isSecurityCopilot = productName?.toLowerCase().includes("security copilot");

  const handleResize = useCallback((width: number) => setWidth(width), []);
  const formatFeaturesCallback = useCallback(
    (data: FeaturesByNameProps[]): ILineChartData => ({
      name: data.at(0)?.featureName || "",
      data: data.map(({ currentValue, snapshotDate }) => ({ x: snapshotDate, y: currentValue }))
    }),
    []
  );

  const formatMDPUFeaturesCallback = useCallback(
    (data: FeaturesByNameProps[]): ILineChartData => ({
      name: data.at(0)?.featureName || "",
      data: data.map(({ currentMDPUValue, snapshotDate }) => ({ x: snapshotDate, y: currentMDPUValue }))
    }),
    []
  );
  const mapPredicate = useCallback(formatFeaturesCallback, []);
  const mdpuMapPredicate = useCallback(formatMDPUFeaturesCallback, []);
  const chartData = useMemo(
    () =>
      getLineChartData(
        Object.values<FeaturesByNameProps[]>(groupBy(data?.featuresByName || [], "featureId")).map(mapPredicate)
      ),
    [data]
  );
  const mdpuChartData = useMemo(
    () =>
      getLineChartData(
        Object.values<FeaturesByNameProps[]>(groupBy(data?.featuresByName || [], "featureId")).map(mdpuMapPredicate)
      ),
    [data]
  );

  useEffect(() => {
    if (lineMetricsContainerRef.current) {
      handleResize(lineMetricsContainerRef.current?.offsetWidth);
    }
  }, [resizedWidth]);

  const customProps = {
    productName: filters?.productName,
    productFilters: JSON.stringify(filters)
  };

  useEffect(() => {
    logEvent("Summary Tab - Features Telemetry", "Product View Page", "SummaryTab", customProps);
  }, [filters]);

  if (isError) {
    return displayAPIsException;
  }

  return isSecurityCopilot ? (
    // Call your custom component for Security Copilot, pass required props
    <div style={{ paddingBottom: 10 }}>
      <GranularSummaryChart
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        error={error}
        width={width}
        data={data}
        kustoLink={data?.featuresByNameKustoLink}
      />
    </div>
  ) : (
    <div style={{ display: "flex" }}>
      <div className={PRODUCT_VIEW_STYLES.TAB.FEATURE_VIEW_SUMMARY_TAB.CLASS_NAMES.featureViewSummaryTabContainer}>
        <CustomCard
          cardHeaderTitle={
            isPurviewProduct
              ? PRODUCT_VIEW_DATA.TABS.TAB.FEATURE_VIEW.SUMMARY_TAB.FEATURE_VIEW_MPU_ALL_UP
              : PRODUCT_VIEW_DATA.TABS.TAB.FEATURE_VIEW.SUMMARY_TAB.FEATURE_VIEW_ALL_UP
          }
          styles={PRODUCT_VIEW_STYLES.TAB.FEATURE_VIEW_SUMMARY_TAB.CSS_STYLES.customCardStyles}
          kustoLink={data?.featuresByNameKustoLink}
        >
          <div ref={lineMetricsContainerRef} style={{ height: 400 }}>
            {isSuccess &&
              ((data?.featuresByName.length && (
                <LineChartComponent
                  allowMultipleShapesForPoints
                  allowResize
                  data={chartData}
                  width={width}
                  height={400}
                />
              )) ||
                (data?.featuresByName.length === 0 && <DisplayEmptyDataMessage />))}
            {isLoading && <CustomShimmer columnCount={4} styles={{ root: { height: 400 } }} />}
          </div>
        </CustomCard>
      </div>
      <div className={PRODUCT_VIEW_STYLES.TAB.FEATURE_VIEW_SUMMARY_TAB.CLASS_NAMES.featureViewSummaryTabContainer}>
        {isPurviewProduct && (
          <CustomCard
            cardHeaderTitle={PRODUCT_VIEW_DATA.TABS.TAB.FEATURE_VIEW.SUMMARY_TAB.FEATURE_VIEW_MDPU_ALL_UP}
            styles={PRODUCT_VIEW_STYLES.TAB.FEATURE_VIEW_SUMMARY_TAB.CSS_STYLES.customCardStyles}
            kustoLink={data?.featuresByNameKustoLink}
          >
            <div style={{ height: 400 }}>
              {isSuccess &&
                ((data?.featuresByName.length && (
                  <LineChartComponent
                    allowMultipleShapesForPoints
                    allowResize
                    data={mdpuChartData}
                    width={width}
                    height={400}
                  />
                )) ||
                  (data?.featuresByName.length === 0 && <DisplayEmptyDataMessage />))}
              {isLoading && <CustomShimmer columnCount={4} styles={{ root: { height: 400 } }} />}
            </div>
          </CustomCard>
        )}
      </div>
    </div>
  );
};
